import { useCallback, useEffect, useMemo, useState } from 'react'
import * as echarts from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerOIDataPoint, ScreenerTickerData } from '@/types'
import { MergedOIChart } from './MergedOIChart'
import { OIPanel } from './OIPanel'
import { PriceLineChart } from './PriceLineChart'
import { exchangeColor, SCREENER_CHART_GROUP } from './chartUtils'
import { useExchangeChartOrder } from './useExchangeChartOrder'
import { useExchangeChartReordering } from './useExchangeChartReordering'
import { useChartCollapsePreference } from './useChartCollapsePreference'
import { ResizableChart } from './ResizableChart'
import { CVDChart } from './CVDChart'
import { ChartPaneFrame } from './ChartPaneFrame'
import { DeferredChartPane } from './DeferredChartPane'
import { FEATURE_SCREENER_TRADES_ENABLED } from '@/lib/featureFlags'
import type { ChartAlertControls } from './ChartLevelAlerts'
import type { ScreenerMetric } from '@/lib/api'
import type { RulerExchangeSeries } from './ChartRuler'
import { useChartLayoutPreferences, type ChartLayoutPreferenceControls } from '@/hooks/useChartLayoutPreferences'

export type ViewMode = 'merged' | 'separate'

interface ChartLayoutManagerProps {
  tickerData: ScreenerTickerData | null
  symbol: string
  mode: ViewMode
  selectedExchanges: string[]
  priceExchange: string
  interval: string
  orderStorageKey: string
  alertControls?: ChartAlertControls
  layoutPreferences?: ChartLayoutPreferenceControls
  oiLineMode?: boolean
  onMetricsChange?: (metrics: ScreenerMetric[]) => void
}
export function ChartLayoutManager(props: ChartLayoutManagerProps) {
  return props.layoutPreferences
    ? <ChartLayoutContent {...props} layoutPreferences={props.layoutPreferences} />
    : <LocalChartLayoutManager {...props} />
}

function LocalChartLayoutManager(props: ChartLayoutManagerProps) {
  const layout = useChartLayoutPreferences(props.orderStorageKey)
  return <ChartLayoutContent {...props} layoutPreferences={layout.controls} />
}

function chartLabel(id: string) {
  if (id === 'price') return 'price'
  if (id === 'merged-oi') return 'merged OI'
  const [metric, exchange] = id.split(':')
  return `${exchange} ${metric.toUpperCase()}`
}

function ChartLayoutContent({
  tickerData,
  symbol,
  mode,
  selectedExchanges,
  priceExchange,
  interval,
  orderStorageKey,
  alertControls,
  layoutPreferences,
  oiLineMode = true,
  onMetricsChange,
}: ChartLayoutManagerProps & { layoutPreferences: ChartLayoutPreferenceControls }) {
  const [syncedTime, setSyncedTime] = useState<Time | null>(null)
  const hidden = useMemo(() => new Set(layoutPreferences.value.hidden || []), [layoutPreferences.value.hidden])
  const setVisible = (id: string, visible: boolean) => layoutPreferences.setHidden(visible
    ? [...hidden].filter(value => value !== id) : [...new Set([...hidden, id])])
  const { isCollapsed, toggleChart } = useChartCollapsePreference(orderStorageKey, {
    collapsed: layoutPreferences.value.collapsed, onChange: layoutPreferences.setCollapsed,
  })
  const paneHeight = (id: string, defaultHeight: number) => ({
    height: layoutPreferences.value.heights[id] ?? defaultHeight,
    onHeightChange: (height: number) => layoutPreferences.setHeight(id, height),
  })
  const handleCrosshairMove = useCallback((time: Time | null) => setSyncedTime(time), [])

  useEffect(() => {
    echarts.connect(SCREENER_CHART_GROUP)
    return () => echarts.disconnect(SCREENER_CHART_GROUP)
  }, [])

  const oiExchanges = useMemo(() => {
    if (!tickerData?.exchanges) return []
    return Object.entries(tickerData.exchanges)
      .filter(([, data]) => tickerData.history_window != null || (data.oi_history?.length || 0) > 0)
      .map(([exchange]) => exchange)
      .sort()
  }, [tickerData])

  const visibleExchanges = useMemo(() => {
    const selected = new Set(selectedExchanges)
    return oiExchanges.filter((exchange) => selected.has(exchange) && !hidden.has(`oi:${exchange}`))
  }, [oiExchanges, selectedExchanges, hidden])

  const cvdExchanges = useMemo(() => [...new Set([
    ...Object.entries(tickerData?.exchanges || {})
      .filter(([, data]) => data.cvd_history !== undefined || data.cvd_error != null).map(([exchange]) => exchange),
    // Keep collapsed panes discoverable when their metric is omitted by reads.
    ...(FEATURE_SCREENER_TRADES_ENABLED ? selectedExchanges.filter(exchange => tickerData?.exchanges[exchange]) : []),
  ])].sort(), [tickerData, selectedExchanges])
  const chartExchanges = useMemo(() => [...new Set([...oiExchanges, ...cvdExchanges])], [oiExchanges, cvdExchanges])
  const chartIds = useMemo(() => [
    'price', 'merged-oi', ...oiExchanges.map((exchange) => `oi:${exchange}`), ...cvdExchanges.map((exchange) => `cvd:${exchange}`),
  ], [oiExchanges, cvdExchanges])
  const { orderedExchanges: orderedChartIds, moveExchange: moveChart } = useExchangeChartOrder(chartIds, orderStorageKey, {
    order: layoutPreferences.value.order, onChange: layoutPreferences.setOrder,
  })
  const visibleChartIds = orderedChartIds.filter((id) => {
    if (hidden.has(id)) return false
    if (id === 'price') return true
    if (id === 'merged-oi') return mode === 'merged' && visibleExchanges.length > 0
    if (id.startsWith('oi:')) return mode === 'separate' && visibleExchanges.includes(id.slice(3))
    return FEATURE_SCREENER_TRADES_ENABLED && selectedExchanges.includes(id.slice(4))
  })
  const availableChartIds = [...new Set(['price', ...(mode === 'merged' ? ['merged-oi'] : []),
    ...selectedExchanges.map(exchange => `oi:${exchange}`),
    ...(FEATURE_SCREENER_TRADES_ENABLED ? selectedExchanges.map(exchange => `cvd:${exchange}`) : [])])]
  const hasVisibleOI = selectedExchanges.some(exchange => !hidden.has(`oi:${exchange}`))
    && (mode !== 'merged' || !hidden.has('merged-oi'))
  const neededMetrics: ScreenerMetric[] = []
  if (!hidden.has('price') && !isCollapsed('price')) neededMetrics.push('price')
  if (hasVisibleOI && selectedExchanges.some(exchange => !hidden.has(`oi:${exchange}`)
    && !isCollapsed(mode === 'merged' ? 'merged-oi' : `oi:${exchange}`))) {
    neededMetrics.push('oi')
    if (FEATURE_SCREENER_TRADES_ENABLED) neededMetrics.push('volume')
  }
  if (FEATURE_SCREENER_TRADES_ENABLED && selectedExchanges.some(exchange => !hidden.has(`cvd:${exchange}`)
    && !isCollapsed(`cvd:${exchange}`))) neededMetrics.push('cvd')
  if (hasVisibleOI) neededMetrics.push('funding') // Header badges remain visible when collapsed.
  const metricKey = neededMetrics.sort().join(',')
  useEffect(() => {
    onMetricsChange?.(metricKey ? metricKey.split(',') as ScreenerMetric[] : [])
  }, [metricKey, onMetricsChange])
  const reorderContext = JSON.stringify([symbol, mode, orderStorageKey])
  const reordering = useExchangeChartReordering(visibleChartIds, moveChart, reorderContext, chartLabel)

  const effectivePriceExchange = useMemo(() => {
    if (priceExchange && tickerData?.exchanges?.[priceExchange]) return priceExchange
    return visibleExchanges[0] || chartExchanges[0] || ''
  }, [priceExchange, tickerData, visibleExchanges, chartExchanges])

  const seriesByExchange = useMemo(() => {
    const result: Record<string, ScreenerOIDataPoint[]> = {}
    for (const exchange of visibleExchanges) {
      result[exchange] = tickerData?.exchanges?.[exchange]?.oi_history || []
    }
    return result
  }, [tickerData, visibleExchanges])

  const rulerExchanges = useMemo<RulerExchangeSeries[]>(() => [...new Set(selectedExchanges)].map((exchange, index) => {
    const data = tickerData?.exchanges?.[exchange]
    const visibleIndex = visibleExchanges.indexOf(exchange)
    return {
      id: exchange,
      label: exchange,
      color: exchangeColor(visibleIndex >= 0 ? visibleIndex : index),
      oiData: data?.oi_history || [],
      candles: data?.candles || [],
      currentPrice: data?.last_price,
    }
  }), [selectedExchanges, tickerData, visibleExchanges])

  const fillThrough = useMemo(() => {
    if (tickerData?.oi_history_until != null) return tickerData.oi_history_until
    // Older servers have no query watermark. Align exchanges only to received
    // chart history, never to the browser clock or unrelated ticker prices.
    const timestamps = Object.values(tickerData?.exchanges || {}).flatMap((data) =>
      (data.oi_history || []).map((point) => point.timestamp).filter(Number.isFinite))
    return timestamps.length > 0 ? timestamps.reduce((latest, value) => Math.max(latest, value), timestamps[0]) : null
  }, [tickerData])

  const visibilityControls = (
      <details className="mb-2 rounded border border-slate-800 px-2 py-1 text-xs text-slate-400" data-chart-visibility>
        <summary className="cursor-pointer select-none text-slate-300">Show charts{availableChartIds.some(id => hidden.has(id))
          ? ` (${availableChartIds.filter(id => hidden.has(id)).length} removed)` : ''}</summary>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 pb-1">
          {availableChartIds.map(id => <label key={id} className="flex cursor-pointer items-center gap-1.5">
            <input type="checkbox" checked={!hidden.has(id)} aria-label={`Show ${chartLabel(id)} chart`}
              onChange={event => setVisible(id, event.target.checked)} />
            {chartLabel(id)}
          </label>)}
        </div>
      </details>
  )

  if (!tickerData) {
    return <div>{visibilityControls}<div role="status" className="flex h-96 items-center justify-center text-sm text-slate-500">Loading charts for {symbol}…</div></div>
  }

  const priceData = effectivePriceExchange ? tickerData.exchanges[effectivePriceExchange] : null

  const renderPane = (id: string) => {
    if (id === 'price') return (
      <ResizableChart
        {...paneHeight('price', 330)}
        storageKey={`${orderStorageKey}:height:price`}
        label="price chart" defaultHeight={330} minHeight={180} collapsed={isCollapsed('price')}
      >
        {(height) => (
          <PriceLineChart
            height={height}
            collapsed={isCollapsed('price')}
            onToggleCollapse={() => toggleChart('price')}
            rulerExchanges={rulerExchanges}
            alertControls={alertControls}
            key={`${symbol}:${effectivePriceExchange}`}
            oiData={priceData?.oi_history || []}
            candles={priceData?.candles || []}
            tradeHistory={priceData?.trade_price_history}
            candleSource={priceData?.candle_source}
            candleError={priceData?.candle_error}
            quoteCurrency={priceData?.candle_quote_currency}
            currentPrice={priceData?.last_price}
            market={priceData?.market === 'spot' ? 'spot' : 'futures'}
            candleInterval={priceData?.candle_interval ?? tickerData.price_interval}
            symbol={symbol}
            exchange={effectivePriceExchange}
            interval={interval}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
          />
        )}
      </ResizableChart>
    )
    if (id === 'merged-oi') return (
      <ResizableChart
        {...paneHeight('merged-oi', 440)}
        storageKey={`${orderStorageKey}:height:merged-oi`}
        label="merged OI chart" defaultHeight={440} minHeight={240} collapsed={isCollapsed('merged-oi')}
      >
        {(height) => (
          <MergedOIChart
            lineMode={oiLineMode}
            height={height}
            collapsed={isCollapsed('merged-oi')}
            onToggleCollapse={() => toggleChart('merged-oi')}
            alertControls={alertControls}
            symbol={symbol}
            key={`${symbol}:${visibleExchanges.join(',')}`}
            seriesByExchange={seriesByExchange}
            exchangeData={tickerData.exchanges}
            candleInterval={tickerData.price_interval}
            interval={interval}
            fillThrough={fillThrough}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
          />
        )}
      </ResizableChart>
    )
    const exchange = id.slice(id.indexOf(':') + 1)
    const data = tickerData.exchanges[exchange]
    if (id.startsWith('oi:')) return (
      <ResizableChart
        {...paneHeight(`oi:${exchange}`, 145)}
        storageKey={`${orderStorageKey}:height:oi:${exchange}`}
        label={`${exchange} OI chart`} defaultHeight={145} minHeight={120} collapsed={isCollapsed(`oi:${exchange}`)}
      >
        {(height) => (
          <OIPanel
            key={`${symbol}:${exchange}`}
            lineMode={oiLineMode}
            height={height}
            collapsed={isCollapsed(`oi:${exchange}`)}
            onToggleCollapse={() => toggleChart(`oi:${exchange}`)}
            alertControls={alertControls}
            symbol={symbol}
            oiData={data.oi_history || []}
            candles={data.candles || []}
            currentPrice={data.last_price}
            candleInterval={tickerData.price_interval}
            currentOI={data.oi_current}
            fundingData={data}
            volumeHistory={data.volume_history}
            volumeInterval={data.volume_interval}
            exchange={exchange}
            interval={interval}
            fillThrough={fillThrough}
            color={exchangeColor(visibleExchanges.indexOf(exchange))}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
            showXAxis
          />
        )}
      </ResizableChart>
    )
    return (
      <ResizableChart
        {...paneHeight(`cvd:${exchange}`, 200)}
        storageKey={`${orderStorageKey}:height:cvd:${exchange}`}
        label={`${exchange} CVD chart`} defaultHeight={200} minHeight={140}
        collapsed={isCollapsed(`cvd:${exchange}`)}>
        {(height) => (
          <CVDChart
            key={`${symbol}:${exchange}`}
            symbol={symbol} exchange={exchange}
            candles={data.cvd_history || []}
            interval={data.cvd_interval || interval}
            unit={data.cvd_unit || 'coins'}
            baselineTimestamp={data.cvd_baseline_ts}
            hasGaps={data.cvd_has_gaps}
            error={data.cvd_error}
            loading={tickerData.pending_metrics?.includes('cvd')}
            height={height}
            collapsed={isCollapsed(`cvd:${exchange}`)}
            onToggleCollapse={() => toggleChart(`cvd:${exchange}`)}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
          />
        )}
      </ResizableChart>
    )
  }

  return (
    <div className="screener-chart-layout w-full min-w-0">
      {visibilityControls}
      <div className="mb-2 flex flex-wrap justify-end gap-1 px-1" aria-label="Chart row layout">
        {([{ label: '3 per row', width: 100 / 3 }, { label: '4 per row', width: 25 },
          { label: 'Full-width rows', width: 100 }]).map(({ label, width }) => (
          <button key={label} type="button" className="rounded border border-slate-700 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300"
            title={`${label} for the current layout, including hidden panes`}
            onClick={() => [...new Set([...Object.keys(layoutPreferences.value.widths), ...availableChartIds])]
              .forEach((id) => layoutPreferences.setWidth(id, width))}>
            {label}
          </button>
        ))}
      </div>
      <div className="mb-2 px-1 text-[10px] text-slate-500">Drag a pane handle or use its arrows to arrange charts · Widths snap to 10% steps, 25% quarters and 33⅓% thirds · Heights snap in 5% steps of the chart area · Double-click a resize handle to reset · Restore removed panes in Show charts · Charts stack in narrow workspaces</div>
      <div className="mb-2 px-1 text-[10px] text-slate-500">Double-click a price or OI graph to set a personal alert · Manage levels and delivery in My alerts · Drag right-axis values to scale, click to fit · Ruler: Shift-click or Shift-drag · Click to finish · Esc to clear · In merged view, select near an exchange line</div>
      {selectedExchanges.length === 0 && (
        <div className="mb-2 flex h-20 items-center justify-center rounded-xl border border-dashed border-slate-800 text-sm text-slate-500">
          Select at least one OI exchange.
        </div>
      )}
      {visibleChartIds.length === 0 && <div className="rounded-xl border border-dashed border-slate-800 px-4 py-8 text-center text-sm text-slate-500">
        All charts are removed from this layout. Restore them with Show charts.
      </div>}
      <div className="screener-chart-grid" data-chart-grid>
        {visibleChartIds.map((id) => (
          <ChartPaneFrame key={id} id={id} label={`${chartLabel(id)} chart`}
            width={layoutPreferences.value.widths[id] ?? 100}
            onWidthChange={(width) => layoutPreferences.setWidth(id, width)}
            onHide={() => setVisible(id, false)}
            dropProps={reordering.getDropProps(id)} orderControls={reordering.renderControls(id)}>
            <DeferredChartPane height={isCollapsed(id) ? 48 : layoutPreferences.value.heights[id]
              ?? (id === 'price' ? 330 : id === 'merged-oi' ? 440 : id.startsWith('cvd:') ? 200 : 145)}>
              {renderPane(id)}
            </DeferredChartPane>
          </ChartPaneFrame>
        ))}
      </div>
    </div>
  )
}
